import { ConcesionesRaw, Concesiones } from "./types/concesiones";
import { fetchWithRetry } from "../client";
import { cmsLang, type Locale } from "@/i18n/config";

/**
 * Listado de concesiones (tiendas, restaurantes, salas VIP, servicios…).
 *
 * Dos cosas que no son evidentes en el endpoint:
 *
 * 1. El CMS sí sabe filtrar por tipo, pero sólo reconoce el nombre exacto de la
 *    columna: `tipo_concesion_codigo`. Con `tipo` o `tipo_concesion` devuelve
 *    los 247 registros como si no hubiera filtro, sin avisar. Antes se pedía
 *    todo y se filtraba aquí, así que Tiendas bajaba 621 KB para usar 114
 *    registros.
 *
 * 2. No hay paginación ni selección de campos: `per_page`, `limit` y `page` se
 *    ignoran y `fields` devuelve un 500. Lo único que reduce el payload hoy es
 *    el filtro por tipo.
 *
 * Encima va una caché con TTL, porque el mismo listado se pide en ocho
 * pantallas distintas y navegar entre ellas repetía la descarga entera.
 */

/** El listado cambia poco; diez minutos evitan servir un horario de ayer. */
const TTL_MS = 10 * 60 * 1000;

type Entrada = { datos: Concesiones[]; hora: number };

const cache = new Map<string, Entrada>();
const enVuelo = new Map<string, Promise<Concesiones[]>>();

/** `tipo` vacío es el listado completo. */
const clave = (lang: string, tipo: string) => `${lang}|${tipo.toLowerCase()}`;

const vigente = (e: Entrada | undefined): e is Entrada =>
  e !== undefined && Date.now() - e.hora < TTL_MS;

function mapear(item: ConcesionesRaw): Concesiones {
  return {
    id: item.id,
    nombre: item.nombre,
    descripcion: item.descripcion,
    foto: item.foto,
    tipoConcesion: item.tipo_concesion_codigo,
    categoria: item.categoria,
    tags: (item.tags ?? []).map((tag) => ({ id: tag.id, nombre: tag.nombre })),
    terminal: item.terminal,
    horarios: (item.horarios ?? []).map((h) => ({
      dias: h.dias,
      hora: h.hora,
    })),
    mapaUrl: item.mapa_url,
    carrusel: item.carrusel,
    detalleUbicacion: item.detalle_ubicacion,
    telefono: item.telefono,
    whatsapp: item.whatsapp,
    email: item.email,
    web: item.web,
  };
}

const porTipo = (datos: Concesiones[], tipo: string) =>
  datos.filter(
    (item) => item.tipoConcesion?.toLowerCase() === tipo.toLowerCase(),
  );

async function pedir(lang: string, tipo: string): Promise<Concesiones[]> {
  const query = new URLSearchParams({ lang });
  if (tipo) query.set("tipo_concesion_codigo", tipo);

  const res = await fetchWithRetry<{ data: ConcesionesRaw[] }>(
    `/v1/concesiones?${query}`,
  );
  const datos = (res.data ?? []).map(mapear);

  // El filtro del CMS se verifica en vez de darse por hecho: si algún día deja
  // de reconocer el parámetro, la pantalla sigue mostrando lo que le toca.
  return tipo ? porTipo(datos, tipo) : datos;
}

/**
 * @param tipoConcesion Código del CMS (`tienda`, `rest`, `salvip`, `servaero`,
 * `servfin`, `transp`, `centinfo`). Sin él se trae el listado completo.
 */
export async function fetchConcesiones(
  lang: Locale,
  tipoConcesion?: string,
): Promise<Concesiones[]> {
  const idioma = cmsLang(lang);
  const tipo = tipoConcesion ?? "";
  const k = clave(idioma, tipo);

  const guardado = cache.get(k);
  if (vigente(guardado)) return guardado.datos;

  // Si el listado completo ya está en caché, un tipo suelto sale de ahí: pedir
  // sólo restaurantes cuando ya tenemos los 247 sería una descarga de más.
  if (tipo) {
    const completo = cache.get(clave(idioma, ""));
    if (vigente(completo)) return porTipo(completo.datos, tipo);
  }

  const yaPedido = enVuelo.get(k);
  if (yaPedido) return yaPedido;

  const promesa = pedir(idioma, tipo)
    .then((datos) => {
      cache.set(k, { datos, hora: Date.now() });
      return datos;
    })
    .finally(() => {
      // Sin esto un fallo dejaría la promesa rechazada pegada al mapa y
      // reintentar desde la UI no volvería a tocar la red.
      enVuelo.delete(k);
    });

  enVuelo.set(k, promesa);
  return promesa;
}

/** Descarta la caché para que la próxima llamada vaya a la red. */
export function invalidarConcesiones(): void {
  cache.clear();
}
